import { useState, useEffect, useRef } from 'react';
import firebase from 'firebase/compat/app';
import { ConfirmModal } from '../common/ConfirmModal';
import { NotificationToast } from '../common/NotificationToast';
import { formatDateDDMMYYYY, formatTimestampDDMMYYYYHHMM, toISODate } from '../../utils/dateHelpers';

const functions = firebase.functions();

/**
 * Task detail modal - view and complete tasks
 * Handles photo uploads, step tracking, and task completion
 */
export function TaskDetailModal({ task, currentUser, locations, users, onClose, onComplete, onResolve }) {
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoComment, setPhotoComment] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [completingTask, setCompletingTask] = useState(false);
  const [notification, setNotification] = useState(null);
  
  // Steps state for task completion
  const [steps, setSteps] = useState(() => {
    if (task.steps && task.steps.length > 0) {
      return task.steps.map(step => ({
        ...step,
        completed: step.completed || false
      }));
    }
    return [];
  });
  
  const [stepsSaving, setStepsSaving] = useState(false);
  const saveTimeoutRef = useRef(null);
  
  // Auto-save steps after 10 seconds of no changes
  useEffect(() => {
    if ((task.status !== 'open' && task.status !== 'overdue') || steps.length === 0) return;
    
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    saveTimeoutRef.current = setTimeout(() => {
      saveSteps();
    }, 10000);
    
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [steps]);
  
  // Save steps on unmount if task is still open (uses latest steps via ref)
  const stepsRef = useRef(steps);
  useEffect(() => { stepsRef.current = steps; }, [steps]);
  useEffect(() => {
    return () => {
      const latest = stepsRef.current;
      if ((task.status === 'open' || task.status === 'overdue') && latest.length > 0 && !uploading && !completingTask) {
        const updateTaskSteps = functions.httpsCallable('updateTaskSteps');
        updateTaskSteps({ taskId: task.id, steps: latest }).catch(() => {});
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  const saveSteps = async () => {
    // Only save if task is still open (not completed or completing)
    if ((task.status !== 'open' && task.status !== 'overdue') || stepsSaving || uploading || completingTask) return;

    setStepsSaving(true);
    try {
      const updateTaskSteps = functions.httpsCallable('updateTaskSteps');
      await updateTaskSteps({
        taskId: task.id,
        steps: steps
      });
    } catch (err) {
      // console statement removed for production
    } finally {
      setStepsSaving(false);
    }
  };
  
  const saveStepsImmediately = () => {
    // Only save if task is still open and not in the process of completing
    if ((task.status !== 'open' && task.status !== 'overdue') || steps.length === 0 || uploading || completingTask) return;

    // Fire and forget - don't wait for response
    const updateTaskSteps = functions.httpsCallable('updateTaskSteps');
    updateTaskSteps({
      taskId: task.id,
      steps: steps
    }).catch((err) => { /* console.error suppressed */ });
  };
  
  const handleStepToggle = (stepId) => {
    setSteps(prevSteps => 
      prevSteps.map(step => 
        step.id === stepId 
          ? { ...step, completed: !step.completed }
          : step
      )
    );
  };
  
  const hasIncompleteSteps = steps.length > 0 && steps.some(s => !s.completed);
  const stepsCompletedCount = steps.filter(s => s.completed).length;
  const stepsTotalCount = steps.length;

  const location = locations.find(l => l.id === task.locationId);
  const assignee = users.find(u => u.id === task.assigneeId);
  const isAssignee = task.assigneeId === currentUser.uid;
  const isAdmin = currentUser.role === 'admin';
  const canComplete = (isAssignee || isAdmin) && (task.status === 'open' || task.status === 'overdue');

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Bitte wählen Sie eine Bilddatei');
      return;
    }

    // Validate file size (max 5MB before compression)
    if (file.size > 5 * 1024 * 1024) {
      setError('Datei ist zu groß (max 5MB)');
      return;
    }

    setPhotoFile(file);
    setError('');

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => setPhotoPreview(e.target.result);
    reader.readAsDataURL(file);
  };

  const compressImage = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          try {
            const canvas = document.createElement('canvas');
            let width = img.width;
            let height = img.height;

            // Calculate new dimensions (max 1920px)
            const maxSize = 1920;
            if (width > height && width > maxSize) {
              height = (height * maxSize) / width;
              width = maxSize;
            } else if (height > maxSize) {
              width = (width * maxSize) / height;
              height = maxSize;
            }

            canvas.width = width;
            canvas.height = height;

            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, width, height);

            // Compress to target size
            let quality = 0.8;
            const compress = () => {
              canvas.toBlob(
                (blob) => {
                  if (!blob) {
                    reject(new Error('Bildkomprimierung fehlgeschlagen'));
                    return;
                  }
                  if (blob.size <= 1024 * 1024 || quality <= 0.1) {
                    resolve(blob);
                  } else {
                    quality -= 0.1;
                    compress();
                  }
                },
                'image/jpeg',
                quality
              );
            };

            compress();
          } catch (e) {
            reject(e);
          }
        };
        img.onerror = () => reject(new Error('Bild konnte nicht geladen werden'));
      };
      reader.onerror = reject;
    });
  };

  const uploadPhoto = async (file) => {
    // Compress image
    const compressedBlob = await compressImage(file);
    
    // Get auth user
    const authUser = firebase.auth().currentUser;
    if (!authUser) {
      throw new Error('Not authenticated');
    }
    
    // Create unique filename (uid + ms + random to avoid same-ms overwrite)
    const timestamp = Date.now();
    const rand = Math.floor(Math.random() * 1e6).toString(36);
    const filename = `${authUser.uid}_${timestamp}_${rand}.jpg`;
    const path = `task-photos/${authUser.uid}/${filename}`;
    
    // Upload to Firebase Storage
    const storageRef = firebase.storage().ref();
    const photoRef = storageRef.child(path);
    
    await photoRef.put(compressedBlob, {
      contentType: 'image/jpeg'
    });

    // Get download URL
    const downloadURL = await photoRef.getDownloadURL();
    
    return downloadURL;
  };

  const handleComplete = () => {
    // Validation
    if (task.photoRequired && !photoFile) {
      setError('Foto ist für diese Aufgabe erforderlich');
      return;
    }
    
    // If steps exist and some are incomplete, comment is mandatory
    if (hasIncompleteSteps && !photoComment.trim()) {
      setError('Kommentar ist erforderlich, wenn nicht alle Schritte abgeschlossen sind');
      return;
    }

    setCompletingTask(true);
  };

  const confirmComplete = async () => {
    setCompletingTask(false);
    setUploading(true);
    setError('');

    try {
      let photoUrl = null;

      // Upload photo if provided
      if (photoFile) {
        photoUrl = await uploadPhoto(photoFile);
      }

      // Complete task via Cloud Function with steps
      const completeTask = functions.httpsCallable('completeTask');
      await completeTask({
        taskId: task.id,
        photoUrl,
        photoComment: photoComment.trim(),
        steps: steps
      });

      setNotification({ message: 'Aufgabe erfolgreich abgeschlossen!', type: 'success' });
      // Firestore listener will update UI automatically
      setTimeout(() => onComplete(), 500);
    } catch (err) {
      // console statement removed for production
      setError(err.message || 'Fehler beim Abschließen der Aufgabe');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{task.title}</h2>
          <button className="modal-close" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="task-detail-content">
          <div className="task-info-section">
            <div className="info-row">
              <span className="info-label"><i className="fas fa-align-left"></i> Beschreibung:</span>
              <span className="info-value">{task.description || 'Keine Beschreibung'}</span>
            </div>

            <div className="info-row">
              <span className="info-label"><i className="fas fa-map-marker-alt"></i> Standort:</span>
              <span className="info-value">{location?.name || 'Unbekannt'}</span>
            </div>

            <div className="info-row">
              <span className="info-label"><i className="fas fa-user"></i> Zuständig:</span>
              <span className="info-value">{assignee?.displayName || 'Unbekannt'}</span>
            </div>

            <div className="info-row">
              <span className="info-label"><i className="fas fa-calendar"></i> Fällig am:</span>
              <span className="info-value">{formatDateDDMMYYYY(task.dueDate)}</span>
            </div>

            <div className="info-row">
              <span className="info-label"><i className="fas fa-info-circle"></i> Status:</span>
              <span className={`badge badge-${task.status} ${task.status === 'completed-with-issues' ? 'with-issues' : ''}`}>
                {task.status === 'open' ? 'Offen' :
                 task.status === 'overdue' ? 'Überfällig' :
                 task.status === 'completed-with-issues' ? '(Teil-)Erledigt' :
                 'Erledigt'}
              </span>
              {(task.status === 'completed' || task.status === 'completed-with-issues') && 
               task.completedAt && 
               (() => { const cd = toISODate(task.completedAt); return cd && task.dueDate < cd; })() && (
                <span className="overdue-badge" style={{ marginLeft: '8px' }}>⚠️ Verspätet</span>
              )}
            </div>

            {task.photoRequired && (
              <div className="info-row">
                <span className="info-label"><i className="fas fa-camera"></i> Foto:</span>
                <span className="info-value">Erforderlich</span>
              </div>
            )}
            
            {steps.length > 0 && (
              <div className="info-row">
                <span className="info-label"><i className="fas fa-tasks"></i> Schritte:</span>
                <span className="info-value">{stepsCompletedCount}/{stepsTotalCount} abgeschlossen</span>
              </div>
            )}
          </div>

          {canComplete && steps.length > 0 && (
            <div className="task-steps-section">
              <h3>Schritte abarbeiten</h3>
              <div className="steps-checklist">
                {steps.map((step) => (
                  <div key={step.id} className="step-checkbox-row">
                    <label className="step-checkbox-label">
                      <input
                        type="checkbox"
                        checked={step.completed}
                        onChange={() => handleStepToggle(step.id)}
                      />
                      <span className={step.completed ? 'completed' : ''}>
                        {step.text}
                      </span>
                    </label>
                  </div>
                ))}
              </div>
              {stepsSaving && (
                <p className="steps-saving-indicator">
                  <i className="fas fa-sync fa-spin"></i> Fortschritt wird gespeichert...
                </p>
              )}
            </div>
          )}

          {canComplete && (
            <div className="task-completion-section">
              <h3>Aufgabe abschließen</h3>

              {task.photoRequired && (
                <div className="form-group">
                  <label>Foto hochladen *</label>
                  <div className="photo-upload">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoChange}
                      id="photo-input"
                      style={{ display: 'none' }}
                    />
                    <label htmlFor="photo-input" className="photo-upload-btn">
                      <i className="fas fa-camera"></i>
                      {photoFile ? 'Anderes Foto wählen' : 'Foto auswählen'}
                    </label>
                  </div>

                  {photoPreview && (
                    <div className="photo-preview">
                      <img src={photoPreview} alt="Preview" />
                      <button
                        type="button"
                        className="photo-remove"
                        onClick={() => {
                          setPhotoFile(null);
                          setPhotoPreview(null);
                        }}
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    </div>
                  )}
                </div>
              )}

              <div className="form-group">
                <label>
                  Kommentar {hasIncompleteSteps && <span className="required-indicator">* (erforderlich bei unvollständigen Schritten)</span>}
                  {!hasIncompleteSteps && '(optional)'}
                </label>
                <textarea
                  value={photoComment}
                  onChange={(e) => setPhotoComment(e.target.value)}
                  placeholder={hasIncompleteSteps 
                    ? "Bitte erklären Sie, warum Schritte nicht abgeschlossen wurden..."
                    : "Notizen oder Anmerkungen zur Aufgabe..."}
                  rows={3}
                  maxLength={500}
                  required={hasIncompleteSteps}
                />
                <span className="char-counter">{photoComment.length}/500</span>
              </div>

              {error && (
                <div className="error-message">{error}</div>
              )}

              <button
                className="btn-success btn-large"
                onClick={handleComplete}
                disabled={uploading || (task.photoRequired && !photoFile) || (hasIncompleteSteps && !photoComment.trim())}
              >
                {uploading ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i> Lädt hoch...
                  </>
                ) : (
                  <>
                    <i className="fas fa-check"></i> Aufgabe abschließen
                  </>
                )}
              </button>
            </div>
          )}

          {(task.status === 'completed' || task.status === 'completed-with-issues') && (
            <div className="task-completed-section">
              <div className={`completed-badge ${task.status === 'completed-with-issues' ? 'with-issues' : ''}`}>
                <i className={task.status === 'completed-with-issues' ? 'fas fa-exclamation-circle' : 'fas fa-check-circle'}></i>
                <span>{task.status === 'completed-with-issues' ? '(Teil-)Erledigt' : 'Erledigt'}</span>
              </div>

              {task.completedAt && (
                <p>Abgeschlossen am: {formatTimestampDDMMYYYYHHMM(task.completedAt)}</p>
              )}
              
              {steps.length > 0 && (
                <div className="completed-steps-summary">
                  <h4>Schritte:</h4>
                  <div className="steps-list-completed">
                    {steps.map((step) => (
                      <div key={step.id} className="step-completed-row">
                        <span className={step.completed ? 'step-completed' : 'step-incomplete'}>
                          <i className={step.completed ? 'fas fa-check' : 'fas fa-times'}></i>
                          {step.text}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {task.photoUrl && (
                <div className="completed-photo">
                  <img src={task.photoUrl} alt="Task completion photo" />
                </div>
              )}

              {task.photoComment && (
                <div className="completed-comment">
                  <strong>Kommentar:</strong>
                  <p>{task.photoComment}</p>
                </div>
              )}
              
              {task.resolutionNote && (
                <div className="resolution-note">
                  <strong>Auflösungsnotiz (Admin):</strong>
                  <p>{task.resolutionNote}</p>
                  {task.resolvedAt && (
                    <p className="resolution-date">
                      Aufgelöst am: {formatTimestampDDMMYYYYHHMM(task.resolvedAt)}
                    </p>
                  )}
                </div>
              )}
              
              {task.status === 'completed-with-issues' && onResolve && (
                <button 
                  className="btn-primary" 
                  onClick={onResolve}
                  style={{ marginTop: '16px' }}
                >
                  <i className="fas fa-check-double"></i> Als aufgelöst markieren
                </button>
              )}
            </div>
          )}
        </div>

        <div className="modal-actions">
          <button type="button" onClick={onClose}>
            Schließen
          </button>
        </div>
      </div>

      {completingTask && (
        <ConfirmModal
          title="Aufgabe abschließen"
          message="Möchten Sie diese Aufgabe als erledigt markieren?"
          confirmText="Als erledigt markieren"
          confirmClass="btn-success"
          onConfirm={confirmComplete}
          onClose={() => setCompletingTask(false)}
        />
      )}

      {notification && (
        <NotificationToast
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}
    </div>
  );
}

// ============================================================================
// TASKS VIEW - Main task overview for all users
// ============================================================================

// ============================================================================
// MULTI-SELECT FILTER - Dropdown with checkboxes
// ============================================================================

